import React, { useContext, useEffect, useRef } from 'react';
import { Typography } from 'app/components/ui';
import { TagWithMetadata } from 'app/api/tag-queries';
import { i18n } from 'app/utils/i18n';
import { TagItemShortcuts } from 'app/views/documents/tagging/TagItemShortcuts/TagItemShortcuts';
import { getTagLabel } from 'app/utils/tag-utils/getTagLabel';
import { TagContext } from 'app/state/contexts/TagContext';
import { useGoToTag } from 'app/utils/hooks/useGoToTag';
import { dispatchMessage, EventType } from 'app/useToasts';

interface TagListItemProps {
  tag: TagWithMetadata;
  selectedTag: TagWithMetadata | null;
  onSelectTag: (tag: TagWithMetadata | null) => void;
  onTagDetailsClick?: (tag: TagWithMetadata) => void;
  onExtensionClick?: (tagId: string) => void;
  onDeleteClick?: (tag: TagWithMetadata) => void;
}

// Map API status values to CSS classes
const originColorClass: Record<string, string> = {
  human: 'all-tags-origin-human',
  ai: 'all-tags-origin-ai',
};

const statusColorClass: Record<string, string> = {
  approved: 'all-tags-status-approved',
  draft: 'all-tags-status-draft',
  verified: 'all-tags-status-verified',
  need_attention: 'all-tags-status-needs-attention',
};

// Map API validationStatus to border classes
const validationBorderClass: Record<string, string> = {
  valid: 'all-tags-item--border-ok',
  unvalidated: 'all-tags-item--border-warning',
  invalid: 'all-tags-item--border-error',
};

export const TagListItem: React.FC<TagListItemProps> = ({ tag, selectedTag, onSelectTag, onTagDetailsClick, onExtensionClick, onDeleteClick }) => {
  const originClass = tag.origin ? originColorClass[tag.origin] : '';
  const statusClass = tag.status ? statusColorClass[tag.status] : '';
  const borderClass = tag.validationStatus ? validationBorderClass[tag.validationStatus] || '' : '';
  const { tagLanguage } = useContext(TagContext);
  const { goToTag, getGoToTagState } = useGoToTag();
  const goToState = getGoToTagState(tag);
  const isSelected = selectedTag?.id === tag.id;
  const label = tag.concept?.labels ? getTagLabel({ languages: tag.concept.labels, currentLang: tagLanguage }) : tag.concept?.name || '-';
  const itemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isSelected]);

  return (
    <li
      ref={itemRef}
      className={`all-tags-item ${borderClass} ${isSelected ? 'all-tags-item--selected' : ''}`}
      tabIndex={0}
      onClick={() => {
        onSelectTag(tag);
        if (goToState.kind === 'scroll') {
          goToTag(tag);
        } else if (goToState.kind === 'navigate') {
          dispatchMessage(EventType.ADD_MESSAGE, {
            type: 'info',
            text: i18n('tag.go.to.tag.other.section'),
            removeTime: 5000,
          });
        }
      }}
      onDoubleClick={() => console.log(`Tag ${tag.id} double clicked`)}
    >
      <div className="all-tags-item__header">
        <div className="all-tags-item__label">
          <Typography variant="body-7">{label || ''}</Typography>
        </div>

        <TagItemShortcuts
          actions={[
            { type: 'details', onClick: () => onTagDetailsClick?.(tag) },
            { type: 'navigate', onClick: () => goToTag(tag), disabled: goToState.disabled, tooltip: goToState.tooltip },
            ...(onExtensionClick ? [{ type: 'create' as const, onClick: () => onExtensionClick(tag.id) }] : []),
            ...(onDeleteClick ? [{ type: 'delete' as const, onClick: () => onDeleteClick(tag) }] : []),
          ]}
        />
      </div>

      <div className="all-tags-item__footer">
        <Typography variant="body-3" fontWeight="light" color="#979797">
          {tag.hidden
            ? i18n('tag.hidden.title')
            : `Sektion: ${tag.metadata?.sectionName} ${tag.metadata?.pageNumber ? `| Sida: ${tag.metadata.pageNumber}` : ''} ${
                tag.metadata?.tableName ? `| Tabell: ${tag.metadata.tableName}` : ''
              }`}
        </Typography>

        <div className="all-tags-item__tag-wrapper">
          <div aria-label="status-tag" className={`all-tags-item__status-tag ${statusClass}`}>
            <Typography variant="body-3" color="inherit">
              {i18n(`tag.status.${tag.status}`)}
            </Typography>
          </div>

          <div aria-label="origin-tag" className={`all-tags-item__origin-tag ${originClass}`}>
            <Typography variant="body-3" color="inherit">
              {i18n(`tag.origin.${tag.origin}`)}
            </Typography>
          </div>
        </div>
      </div>
    </li>
  );
};
